import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebase';
import { RecaptchaVerifier, signInWithPhoneNumber, updatePhoneNumber, PhoneAuthProvider } from "firebase/auth";
import { deleteField, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiPhone, FiCheckCircle, FiAlertCircle, FiLoader, FiAlertTriangle, FiArrowLeft } from 'react-icons/fi';
import { findUserByField, callMergeFunction, requestEmailUpdate, setPendingMerge, healPendingEmailUpdates } from '../../services/ProfileService';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal/Modal';
import './AccountSettings.css'; // Specific styles for account settings

const AccountSettingsPage = () => {
  const { currentUser } = useAuth() || {};
  const user = auth.currentUser || currentUser;
  const navigate = useNavigate();

  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phoneNumber?.replace('+91', '') || '');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('IDLE');
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [emailVerificationSentTo, setEmailVerificationSentTo] = useState(null);
  const [checkingEmailVerification, setCheckingEmailVerification] = useState(false);

  const [mergeData, setMergeData] = useState({ show: false, targetUid: null, type: null, credential: null });
  const currentEmail = user?.email || profile?.email || '';
  const isCurrentEmailVerified = !!(user?.emailVerified || profile?.emailVerified || profile?.isEmailVerified);

  const closeMergeModal = () => {
    setMergeData({ show: false, targetUid: null, type: null, credential: null });
  };

  useEffect(() => {
    if (!user) return;

    if (!emailVerificationSentTo) {
      setEmail(user.email || profile?.email || '');
    }

    setPhone(user.phoneNumber?.replace('+91', '') || profile?.phone_number?.replace('+91', '') || '');
  }, [emailVerificationSentTo, profile?.email, profile?.phone_number, user]);

  useEffect(() => {
    if (!user?.uid) return undefined;

    return onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : null;
      setProfile(data);

      if (data?.pendingEmail && !data?.pendingMergeUid) {
        updateDoc(doc(db, 'users', user.uid), {
          pendingEmail: deleteField(),
          emailChangeRequestedAt: deleteField()
        }).catch((error) => {
          console.error('Failed to clear invalid pending email state', error);
        });
        return;
      }

      if (emailVerificationSentTo && data?.email === emailVerificationSentTo) {
        setEmailVerificationSentTo(null);
        setEmail(data.email);
      }
    }, (error) => {
      console.error('Failed to watch account profile', error);
    });
  }, [emailVerificationSentTo, user?.uid]);

  const syncVerifiedEmailToProfile = useCallback(async () => {
    if (!auth.currentUser || !user?.uid) return false;

    await auth.currentUser.reload();
    const refreshedUser = auth.currentUser;
    const verifiedEmail = refreshedUser?.email || '';

    if (!verifiedEmail || !refreshedUser.emailVerified) return false;

    await updateDoc(doc(db, 'users', user.uid), {
      email: verifiedEmail,
      emailVerified: true,
      isEmailVerified: true
    });

    setEmail(verifiedEmail);
    setEmailVerificationSentTo(null);
    return true;
  }, [user?.uid]);

  const checkEmailVerificationStatus = useCallback(async () => {
    if (!emailVerificationSentTo) return;

    setCheckingEmailVerification(true);
    try {
      const synced = await syncVerifiedEmailToProfile();
      if (synced) {
        alert('Email verified!');
      }
    } catch (e) {
      console.error('Email verification refresh failed', e);
    } finally {
      setCheckingEmailVerification(false);
    }
  }, [emailVerificationSentTo, syncVerifiedEmailToProfile]);

  // --- LOGIC: MERGE HEALER (Checks on tab focus) ---
  const runMergeHealer = useCallback(async () => {
    if (!user || !profile?.pendingEmail || !profile?.pendingMergeUid) return;

    setHealing(true);
    try {
      const result = await healPendingEmailUpdates();
      if (result.success) {
        alert(result.merged ? "Accounts merged successfully!" : "Email verified!");
        window.location.reload();
      }
    } catch (e) { console.error(e); }
    finally { setHealing(false); }
  }, [profile?.pendingEmail, profile?.pendingMergeUid, user]);

  useEffect(() => {
    const handleFocus = () => {
      checkEmailVerificationStatus();
      runMergeHealer();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkEmailVerificationStatus, runMergeHealer]);

  useEffect(() => {
    if (!emailVerificationSentTo) return undefined;

    const intervalId = window.setInterval(() => {
      checkEmailVerificationStatus();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [checkEmailVerificationStatus, emailVerificationSentTo]);

  // --- LOGIC: EMAIL SAVE (Verification/Merge Start) ---
  const handleEmailSave = async () => {
    if (!auth.currentUser || !user) {
      alert('Please sign in again to update your email.');
      return;
    }

    if (email === currentEmail) return;
    setLoading(true);
    try {
      const existingUser = await findUserByField('email', email);
      if (existingUser) {
        setMergeData({ show: true, targetUid: existingUser.uid, type: 'EMAIL', credential: null });
      } else {
        await requestEmailUpdate(email);
        setEmailVerificationSentTo(email);
      }
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  // --- LOGIC: PHONE FLOW ---
  const handlePhoneVerify = async () => {
    if (!auth.currentUser) {
      alert('Please sign in again to update your phone number.');
      return;
    }

    const fullPhone = `+91${phone}`;
    setLoading(true);
    try {
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      const confirmation = await signInWithPhoneNumber(auth, fullPhone, verifier);
      setStep('OTP_SENT');
      window.confirmationResult = confirmation;
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  const handleOtpSubmit = async () => {
    if (!auth.currentUser || !user) {
      alert('Please sign in again to update your phone number.');
      return;
    }

    setLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(window.confirmationResult.verificationId, otp);
      const fullPhone = `+91${phone}`;
      const existingUser = await findUserByField('phone_number', fullPhone);

      if (existingUser) {
        setMergeData({ show: true, targetUid: existingUser.uid, type: 'PHONE', credential: credential });
      } else {
        await updatePhoneNumber(auth.currentUser, credential);
        await updateDoc(doc(db, 'users', user.uid), { phone_number: fullPhone, isPhoneVerified: true });
        alert("Phone number updated!");
        setStep('IDLE');
      }
    } catch (e) { alert("Verification failed: " + e.message); }
    finally { setLoading(false); }
  };

  // --- LOGIC: EXECUTE MERGE ---
  const executeMerge = async () => {
    if (!auth.currentUser || !user) {
      alert('Please sign in again to continue.');
      return;
    }

    setLoading(true);
    try {
      if (mergeData.type === 'EMAIL') {
        await setPendingMerge(user.uid, email, mergeData.targetUid);
        await requestEmailUpdate(email);
        setMergeData({ show: false, targetUid: null, type: null, credential: null });
      } else {
        await callMergeFunction(mergeData.targetUid);
        await updatePhoneNumber(auth.currentUser, mergeData.credential);
        alert("Accounts merged successfully!");
        navigate('/profile');
      }
    } catch (e) { alert("Merge Error: " + e.message); }
    finally { setLoading(false); }
  };

  if (!user) {
    return (
      <div className="account-settings-page-container">
        <button className="back-to-profile" onClick={() => navigate('/profile')}>
          <FiArrowLeft /> Back to Profile
        </button>
        <div className="account-settings-header-card">
          <h2>Account Security</h2>
          <p>Please sign in to manage your email and phone.</p>
        </div>
      </div>
    );
  }

  return (

    <div className="account-settings-page-container">
      <button className="back-to-profile" onClick={() => navigate('/profile')}>
        <FiArrowLeft /> Back to Profile
      </button>
      <div className="account-settings-header-card">
        <h2>Account Security</h2>
        <p>Sync your email and phone across Qinto</p>

        {/* Email Input */}
        <div className="settings-group">
          <label><FiMail /> Email Address</label>
          <div className="input-with-action">
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
            {emailVerificationSentTo && email === emailVerificationSentTo ? (
              <button disabled>{loading ? <FiLoader className="spin" /> : 'Sent'}</button>
            ) : email !== currentEmail ? (
              <button onClick={handleEmailSave} disabled={loading}>
                {loading ? <FiLoader className="spin" /> : 'Verify'}
              </button>
            ) : isCurrentEmailVerified ? (
              <FiCheckCircle style={{ color: 'green', marginRight: '10px' }} />
            ) : (
              <button onClick={handleEmailSave} disabled={loading}>
                {loading ? <FiLoader className="spin" /> : 'Verify'}
              </button>
            )}
          </div>
          {emailVerificationSentTo && (
            <div className="pending-verification-notice">
              <FiAlertCircle />
              <div>
                <p>
                  Verification link sent to {emailVerificationSentTo}.{' '}
                  <button className="refresh-link" onClick={checkEmailVerificationStatus} disabled={checkingEmailVerification}>
                    {checkingEmailVerification ? 'Checking...' : 'Refresh Status'}
                  </button>
                </p>
              </div>
            </div>
          )}
          {profile?.pendingEmail && profile?.pendingMergeUid && (
            <div className="pending-verification-notice">
              <FiAlertCircle />
              <div>
                <p>
                  Merge verification pending for {profile.pendingEmail}.{' '}
                  <button className="refresh-link" onClick={runMergeHealer} disabled={healing}>
                    {healing ? 'Checking...' : 'Refresh Status'}
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Phone Input */}
        <div className="settings-group">
          <label><FiPhone /> Phone Number</label>
          <div className="input-with-action">
            <span style={{ marginRight: '8px', fontWeight: 'bold' }}>+91</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={step === 'OTP_SENT'} />
            {`+91${phone}` !== (user.phoneNumber || profile?.phone_number) && step === 'IDLE' && <button onClick={handlePhoneVerify}>Verify</button>}
          </div>
        </div>

        {/* OTP Input */}
        {step === 'OTP_SENT' && (
          <div className="settings-group animate-in">
            <label>6-Digit OTP</label>
            <div className="input-with-action">
              <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="000000" />
              <button onClick={handleOtpSubmit}>Link Phone</button>
            </div>
          </div>
        )}
      </div>

      {/* Merge Confirmation Modal */}
      <Modal
        isOpen={mergeData.show}
        onClose={closeMergeModal}
        title="Merge with existing account?"
        message={`This ${mergeData.type?.toLowerCase() || 'account detail'} is already in use. Merging will combine your ads and chats into one account.`}
        size="medium"
        icon={<FiAlertTriangle size={48} color="#f59e0b !important" />}
        buttons={[
          { label: loading ? 'Merging...' : 'Yes, Merge', className: 'primary', onClick: executeMerge },
          { label: 'Cancel', onClick: closeMergeModal }
        ]}
      />
      <div id="recaptcha-container"></div>
    </div>
  );
};

export default AccountSettingsPage;
